import clsx from 'clsx'

import { useMemo } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/searchspring/components/Badge'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { ResultsSwatches } from 'libs/searchspring/components/ResultSwatches'

const DEFAULT_OPTIONS = {
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0', eager = false, imageSizes, imageWidths }) {
  const {
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges
  } = options
  const { currentResult, updateSwatch } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  const criteria = {
    tags: currentResult?.tags ?? [],
    collections: currentResult?.collection_handle ?? [],
    products: [currentResult.handle]
  }

  const [firstBadge, secondBadge] = useBadges(criteria)
  const [firstPromoline, secondPromoline] = usePromolines(criteria)

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-main.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DON'T REMOVE THAT DIV 🔫
  return (
    <div className={clsx('group/card relative flex', className)} id={`result__${result.handle}`} data-dl-track="select_item" data-dl-product-id={currentResult.id} data-dl-index={dataIndex}>
      <div className="flex flex-col gap-y-0.625 w-full grow">
        <div className="relative w-full overflow-hidden aspect-4/5">
          <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
            <ResultImage result={currentResult} eager={eager} sizes={imageSizes} widths={imageWidths}>
              {!hideBadges && (firstBadge || secondBadge) && (
                <div className="absolute top-0.5 left-0.5 md:top-1 md:left-1 flex flex-col gap-y-0.125 max-w-[80%]">
                  {firstBadge && <Badge badge={firstBadge} badgeLocation="PLP" product={currentResult} />}
                  {secondBadge && <Badge badge={secondBadge} badgeLocation="PLP" product={currentResult} />}
                </div>
              )}
            </ResultImage>
          </a>
        </div>
        <div className='grow flex flex-col gap-y-0.5 px-0.5'>
          {!hideSwatches && <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />}
          <a href={url} className="grow paragraph-s font-medium link-fade" aria-label={currentResult.name}>
            {currentResult.name}
          </a>
          <Price price={currentResult.variants[0].price} compareAtPrice={currentResult.variants[0].compare_at_price} className="flex gap-x-0.125" />
          {!hidePromolines && (firstPromoline || secondPromoline) && (
            <div className="flex flex-col gap-0.25">
              {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="PLP" promolineSize="medium" />}
              {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="PLP" promolineSize="medium" />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
